import store from '@/store';
import get from 'lodash/get';
import pullAt from 'lodash/pullAt';
import cloneDeep from 'lodash/cloneDeep';
import ApiService from '../../../services/apiService';
import NiotaClassicApiService from '../../../services/niotaClassicApiService';
import { logMessage } from '../../../sentry';
import { themeDefaults, themeColors } from '../../../utils/themes';

const getAllManageTenantScopes = (store, permissions) => {
  // find the user-accesses that have all.manage permission
  const allManageAccesses = (get(store, 'state.authentication.access') || [])
    .filter((access) => {
      return access.roles.find((role) => {
        return role.Rules.find((rule) => {
          return rule.actions.indexOf('manage') >= 0 && rule.subject.indexOf('all') >= 0;
        });
      });
    })
    .map((a) => {
      return { tenantId: a.tenantId, scopeId: a.scopeId };
    });

  // process all access-es to find those that have `all.manage` permission on tenant-scope level
  // AND are top-most in the (from this user's accessible) tenant-hierarchy
  return store
    .dispatch('app/loadAccounts')
    .then((tenants) => {
      let tenantsWithAllManage = tenants.filter((tenant) => {
        // filter for the accesses which's scope is the root-scope of the respective tenant
        return allManageAccesses.findIndex((a) => tenant.scopeId === a.scopeId) >= 0;
      });

      // now make sure to get the upmost ROOT tenants, filter out tenants that children of others
      // (at least those, that this user has access to)
      const isDescendantOfOtherTenant = (tenantId, allManageTenants, tenantList) => {
        // STEP 1: find parent of `tenantId`
        const findParent = (tenantId, tenantList) => {
          return tenantList.find((tenant) => {
            if ((tenant.children || []).length > 0) {
              if (tenant.children.find((childId) => childId === tenantId)) {
                return true;
              }
            }
            return false;
          });
        };
        // trival case: does not have an (accessible) parent, or parent is in allManageTenants already
        const parent = findParent(tenantId, tenantList);
        if (!parent) {
          return false;
        }
        if (allManageTenants.find((t) => t.id === parent.id)) {
          return true;
        }

        // STEP 2: create list of ancestors for that parent
        const findAncestors = (tenantId, tenantList, ancestorList) => {
          const parent = findParent(tenantId, tenantList);
          if (parent) {
            ancestorList.push(parent);
            findAncestors(parent.id, tenantList, ancestorList);
          }
        };
        const ancestorList = [];
        findAncestors(parent.id, tenantList, ancestorList);

        // STEP 3: check if any ancestor is in allManageTenants
        return ancestorList.find((ancestorTenant) => {
          return allManageTenants.find((t) => t.id === ancestorTenant.id);
        });
      };
      for (let i = 0; i < tenantsWithAllManage.length; i++) {
        let otherTenants = cloneDeep(tenantsWithAllManage);
        pullAt(otherTenants, [i]);
        if (isDescendantOfOtherTenant(tenantsWithAllManage[i].id, otherTenants, tenants)) {
          tenantsWithAllManage = otherTenants;
          i--;
        }
      }
      permissions['all.manage.tenantIds'] = tenantsWithAllManage.map((t) => t.id);
      return tenantsWithAllManage.length > 0;
    })
    .catch((error) => {
      console.error(error);
    });
};

const setupTheme = (store, theme, mode) => {
  const fullUrl = (item, defaultValue) => {
    let returnValue = item || defaultValue;
    if (returnValue.startsWith(process.env.VUE_APP_API_PATH)) {
      returnValue = `${process.env.VUE_APP_API_HOST}${returnValue}`;
    }
    return returnValue;
  };
  const getLocalUserColor = (storageKey, fallback = 'primary') => {
    const localColor = localStorage.getItem(storageKey);
    if (themeColors.indexOf(localColor) >= 0) {
      return localColor;
    }

    return fallback;
  };

  const bust = Date.now();
  const addBuster = (url) => {
    if (!url) {
      return url;
    }
    const cleaned = url
      .replace(/([?&])_=[^&]*/g, '$1')
      .replace(/\?&/, '?')
      .replace(/&{2,}/g, '&');
    const sep = cleaned.indexOf('?') >= 0 ? '&' : '?';
    return `${cleaned}${sep}_=${bust}`;
  };

  const themes = {
    dark: {
      background: addBuster(fullUrl(theme.darkBackground, themeDefaults.darkBackground)),
      logo: addBuster(fullUrl(theme.darkLogo, themeDefaults.darkLogo)),
      orientation: theme.darkLogoOrientation || themeDefaults.darkLogoOrientation,
      position: theme.darkLogoPosition || themeDefaults.darkLogoPosition,
      byline: theme.darkLogoByline || themeDefaults.darkLogoByline,
      highlight: theme.darkHighlight || themeDefaults.darkHighlight,
      highlightCustomer: theme.darkHighlightCustomer,
    },
    light: {
      background: addBuster(fullUrl(theme.lightBackground, themeDefaults.lightBackground)),
      logo: addBuster(fullUrl(theme.lightLogo, themeDefaults.lightLogo)),
      orientation: theme.lightLogoOrientation || themeDefaults.lightLogoOrientation,
      position: theme.lightLogoPosition || themeDefaults.lightLogoPosition,
      byline: theme.lightLogoByline || themeDefaults.lightLogoByline,
      highlight: theme.lightHighlight || themeDefaults.lightHighlight,
      highlightCustomer: theme.lightHighlightCustomer,
    },
  };
  store.state.app.themes = themes;

  store.state.app.drawerLinks = theme.drawerLinks;
  store.state.app.mode = mode;
  // persist the original mode of the theme (from tenant-settings)
  // to be able to reset it in `Filter.vue` if user wants everything to be reset to said tenant-settings
  store.state.app.themeMode = theme.mode || themeDefaults.mode;
  store.state.app.UiTheme = mode === 'dark';
  localStorage.setItem('thingshiveTheme', mode);

  const darkHighlight = getLocalUserColor('thingshiveColorDark', themes.dark.highlight);
  const lightHighlight = getLocalUserColor('thingshiveColorLight', themes.light.highlight);

  // highlight
  store.state.app.color = mode === 'dark' ? darkHighlight : lightHighlight;
  store.state.app.colorHexLight = themes.light.highlightCustomer;
  store.state.app.colorHexDark = themes.dark.highlightCustomer;
};

const actions = {
  updateVirtualDeviceListSearch({ commit }, { items, operator }) {
    commit('virtualDeviceListSearch', { items, operator });
  },
  loadPermissions({ commit }) {
    return ApiService.fetchModuleRules().then((moduleRules) => {
      moduleRules.forEach((rule) => {
        rule.exactRule = true;
      });

      let checkingPermissions = [
        // speciality: only 'all.manage' ON the UPMOST ROOT-scope/account will get a true returned
        // the other 'all.manage' permissions could be on any scope
        // to be clear: this permission/rule does not exist!!
        // is just an artificial workaround to conveniently check for system.super.admin
        { subject: 'super.system', action: 'manage' },
        // n1 Admin
        { subject: 'all', action: 'manage' },
        // n1 User
        { subject: 'IotHub', action: 'manage' },
        // "n1 Viewer"
        { subject: 'IotHub', action: 'read' },
        { subject: 'UserManagement', action: 'create' },
        { subject: 'UserManagement', action: 'read' },
        { subject: 'UserManagement', action: 'manage' },
        // should not be included in `UserManagement.manage`
        // exactRule flag should not reaaally be needed, as it is also filtered in the backend, but just to make it clear here
        { subject: 'UserManagement', action: 'impersonate', exactRule: true },
        { subject: 'DigitalTwin', action: 'list' },
        { subject: 'DigitalTwin', action: 'read' },
        { subject: 'DigitalTwin', action: 'write' },
        { subject: 'DigitalTwin', action: 'manage' },
        { subject: 'DigitalTwinStates', action: 'read' },
        { subject: 'VirtualDevice', action: 'list' },
        { subject: 'VirtualDevice', action: 'read' },
        { subject: 'VirtualDevice', action: 'manage' },

        { subject: 'DeviceDriver', action: 'read' },
        { subject: 'DeviceDriver', action: 'manage' },

        { subject: 'DeviceTemplate', action: 'read' },
        { subject: 'DeviceTemplate', action: 'manage' },

        { subject: 'Dashboards', action: 'read' },
        { subject: 'Dashboards', action: 'manage' },
        ...moduleRules,
      ];

      return ApiService.checkAccesses(checkingPermissions).then(async (checkedPermissions) => {
        // the 'all.manage' permission will be overwritten to be only on tenant-root-scope level
        // but the user-list checks for that permission also as first filter to impersonate users,
        // so have this "fallback" here
        checkedPermissions['UserManagement.impersonate'] =
          checkedPermissions['UserManagement.impersonate'] || checkedPermissions['all.manage'];
        if (checkedPermissions['all.manage']) {
          // all.manage permissions should only be valid if on tenant-scope-level
          checkedPermissions['all.manage'] = await getAllManageTenantScopes(store, checkedPermissions);
        }
        commit('app_permissions', {
          permissions: checkedPermissions,
        });
      });
    });
  },
  async loadBranding(_, params) {
    const { hostUri, tenantId, userThemeMode } = params || {};
    let domainTheme = null;

    try {
      domainTheme = await ApiService.getTheming(encodeURIComponent(hostUri), tenantId);
    } catch (error) {
      logMessage(`getTheming failed. ${error.toString()}`);
    }

    const theme = (domainTheme && domainTheme.theme) || {};
    const usedThemeMode = userThemeMode || theme.mode || themeDefaults.mode;
    setupTheme(store, theme, usedThemeMode);

    // page title
    const pageTitle = {
      de: theme.pageTitleDE || themeDefaults.pageTitle,
      en: theme.pageTitleEN || themeDefaults.pageTitle,
    };
    store.state.app.pageTitle = pageTitle;

    // footer
    const footer = {
      showFooterPublicDashboard: theme.showFooterPublicDashboard,
      hide: theme.hideFooter,
      de: theme.footerDE || {},
      en: theme.footerEN || {},
    };
    store.state.app.footer = footer;

    return theme;
  },
  loadAccounts({ commit }, params) {
    const useN1Accounts = (params || {}).n1Accounts;
    const TTL = 30 * 60 * 1000; // time to live = 1800000 ms = 30 minutes

    const lastFetched = useN1Accounts ? store.state.app.n1Accounts.lastFetched : store.state.app.n2Accounts.lastFetched;
    const fetchedAgo = Date.now() - new Date(lastFetched).getTime();

    if (fetchedAgo < TTL) {
      // if already fetched within the last 30 minutes, return the cached accounts
      return useN1Accounts
        ? Promise.resolve(store.state.app.n1Accounts.accounts)
        : Promise.resolve(store.state.app.n2Accounts.accounts);
    }

    const apiCallPromise = useN1Accounts ? NiotaClassicApiService.accountsFetchPlain : ApiService.tenantFetchAll;
    const mutationName = useN1Accounts ? 'n1Accounts' : 'n2Accounts';
    const storedPromiseName = `${mutationName}Promise`;

    if (!actions.loadAccounts[storedPromiseName]) {
      // if not already fetching, fetch the accounts
      actions.loadAccounts[storedPromiseName] = apiCallPromise()
        .then((response) => {
          // commit the accounts to the store
          return commit(mutationName, { accounts: useN1Accounts ? response.data.accounts : response.tenants });
        })
        .then(() => {
          delete actions.loadAccounts[storedPromiseName]; // Reset the promise
          return store.state.app[mutationName].accounts;
        })
        .catch((error) => {
          delete actions.loadAccounts[storedPromiseName]; // Reset the promise
          throw error; // rethrow the error to be handled by the caller
        });
    }

    // return the promise
    return actions.loadAccounts[storedPromiseName];
  },
};

export default actions;
